import { setAlert } from "./AlertSlice";
import alertStore from "./AlertStore";

export const FetchMixin = (superclass) => class extends superclass {

    /**
     * メソッドとエンドポイントを判定し、必要な場合は /rest/ を /read/ に置換したURLを返す
     * @param {string} endpoint 
     * @param {object} options 
     * @returns {string} 解決されたフルURL
     */
    _getResolvedUrl(endpoint, options) {
        let endpoint_base = process.env.ENDPOINT_BASE; // 例: http://localhost/rest/

        const method = (options && options.method) ? options.method.toUpperCase() : 'GET';

        if (method === 'GET') {
            // SQLiteキャッシュ(Read用)に対応しているエンドポイントのリスト
            const readEndpoints = [
                process.env.POINTS_ENDPOINT,
                process.env.POINTS_ENDPOINT_HISTORY,
                process.env.ADMAX_VIEW_STATUS_ENDPOINT,
                process.env.MODERATION_STATUS_ENDPOINT,
                process.env.APPLICATION_LIST_ENDPOINT,
                process.env.USED_DISK_SPACE_ENDPOINT,
                process.env.USER_SETTING_ENDPOINT,
                process.env.ARCHIVE_LIST_ENDPOINT,
                process.env.PUBLISHE_LIST_ENDPOINT,
                process.env.AI_CODE_ENDPOINT,
                process.env.FILE_LIST_ENDPOINT,
                process.env.EDITOR_JSON_ENDPOINT,
                process.env.VERSION_LIST_ENDPOINT,
                process.env.AI_AGENT_STATUS,
                process.env.NOTIFICATION_ENDPOINT,
                process.env.DASHBOARD_INIT_ENDPOINT,
            ].filter(Boolean); // 設定されていない(undefined)ものを除外

            const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
            const isReadTarget = readEndpoints.some(ep => cleanEndpoint.startsWith(ep));

            if (isReadTarget) {
                // 対象であれば /rest/ を /read/ に置換
                endpoint_base = endpoint_base.replace('/rest/', '/read/');
            }
        }

        // パスのスラッシュ重複を防止して結合
        if (endpoint_base.endsWith('/') && endpoint.startsWith('/')) {
            return endpoint_base + endpoint.substring(1);
        } else if (!endpoint_base.endsWith('/') && !endpoint.startsWith('/')) {
            return endpoint_base + '/' + endpoint;
        } else {
            return endpoint_base + endpoint;
        }
    }

    /**
     * 一時的なサーバーエラー(502, 503, 504, 507)やネットワークエラー時にリトライを行うfetchラッパー
     * @param {string} url 
     * @param {object} options 
     * @param {number} maxRetries 最大リトライ回数 (デフォルト: 4)
     * @returns {Promise<Response>}
     */
    async _fetchWithRetry(url, options, maxRetries = 4) {
        let retries = 0;

        while (retries <= maxRetries) {
            try {
                const response = await fetch(url, options);

                // 503 Service Unavailable, 502 Bad Gateway, 504 Gateway Timeout など
                // インフラレベルの一時的なエラーの場合はリトライを試みる
                if (!response.ok && [502, 503, 504, 507].includes(response.status)) {
                    if (retries < maxRetries) {
                        retries++;
                        // リトライ間隔を倍々に伸ばす (1回目:1秒, 2回目:2秒, 3回目:4秒)
                        const delay = Math.pow(2, retries - 1) * 1000;
                        console.warn(`[Fetch] ${response.status} error detected. Retrying in ${delay}ms... (${retries}/${maxRetries})`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                        continue;
                    } else {
                        // 最大リトライ回数を超えた場合は、そのままレスポンスを返す（後続でエラー処理）
                        return response;
                    }
                }

                // 成功、または一時的ではないエラー(400, 401, 403, 404, 500等)はそのまま返す
                return response;

            } catch (error) {
                // ネットワークエラー (DNSエラー、通信断など) の場合
                if (retries < maxRetries) {
                    retries++;
                    const delay = Math.pow(2, retries - 1) * 1000;
                    console.warn(`[Fetch] Network error detected. Retrying in ${delay}ms... (${retries}/${maxRetries})`, error);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                } else {
                    throw error;
                }
            }
        }
    }

    /**
     * 認証済みのエンドポイントにフェッチ
     * @param {*} endpoint 
     * @param {*} options 
     * @returns 
     */
    async fetchWithAuth(endpoint, options) {
        //クッキーからpwappy_loginを取得
        const loginCookieName = process.env.LOGIN_COOKIE_NAME;
        const isLogin = document.cookie.split(';').some((item) => item.trim().startsWith(loginCookieName + '='));
        if (isLogin) {
            if (!options['mode']) {
                options['mode'] = 'cors'
            }
            if (!options['credentials']) {
                options['credentials'] = 'include'
            }
            //optionsのheadersを設定
            if (!options['headers']) {
                options['headers'] = {}
            }
            if (!options['headers']['Content-Type']) {
                options['headers']['Content-Type'] = 'application/json'
            }

            try {
                const url = this._getResolvedUrl(endpoint, options);

                // 通常のfetchの代わりにリトライ付きフェッチを使用
                const response = await this._fetchWithRetry(url, options);

                // Nginxエラーページ（HTML）をJSONパースしてSyntaxErrorになるのを防ぐため、
                // リトライを尽くしても50xの一時エラーだった場合は明示的に例外を投げる
                if (!response.ok && [502, 503, 504].includes(response.status)) {
                    throw new Error(`Server temporarily unavailable (Status: ${response.status})`);
                }

                const json = await response.json(); // JSON のレスポンスを待つ

                if (json && json.data && json.data.status) {
                    if (json.data.status === 'app_token_error') {
                        const loginCookieName = process.env.LOGIN_COOKIE_NAME;
                        document.cookie = `${loginCookieName}=; max-age=0; path=/`;
                        window.location.href = process.env.DEV_URL_BASE;
                    } else if (
                        json.data.status === 'user_suspended' ||
                        json.data.status === 'user_deleted' ||
                        json.data.status === 'user_banned'
                    ) {
                        alertStore.dispatch(setAlert(json.message));
                        return Promise.reject(json.message);
                    }
                } else if (json.code !== 200) { // API エラーの場合
                    if (json.status === 'token_expired_dashboard_init') {
                        return Promise.reject(new Error('Token expired for dashboard-init'));
                    }
                    // エラーメッセージを alert で表示
                    console.error(`[API Error Detail] Status: ${json.status}, Message: ${json.message}, Details:`, json.details);
                    alertStore.dispatch(setAlert(json.message || 'API エラーが発生しました'));
                    // エラーを reject して呼び出し元に伝える
                    return Promise.reject(new Error(json.message || 'API エラーが発生しました'));
                } else {
                    return Promise.resolve(json); // 成功時は json を返す
                }
            } catch (error) {
                console.error(`[Network/Fetch Error]:`, error);
                alertStore.dispatch(setAlert('エラーが発生しました\nしばらくしてから再試行してください'));
                return Promise.reject(error); // ネットワークエラーなどは error を reject
            }
        } else {
            //alertStore.dispatch(setAlert('トークンが有効ではありません\nもう一度ログインしてください'));
            //window.location.reload();
            //process.env.DEV_URL_BASEに画面遷移する
            //window.location.href = process.env.DEV_URL_BASE;
        }
    }

    /**
     * 認証済みのエンドポイントにフェッチ (レスポンスエラーのハンドリングがシンプルな版)
     * @param {*} endpoint 
     * @param {*} options 
     * @returns 
     */
    async fetchWithAuthPlain(endpoint, options) {
        //クッキーからpwappy_loginを取得
        const loginCookieName = process.env.LOGIN_COOKIE_NAME;
        const isLogin = document.cookie.split(';').some((item) => item.trim().startsWith(loginCookieName + '='));
        if (isLogin) {
            try {
                const url = this._getResolvedUrl(endpoint, options);

                // 通常のfetchの代わりにリトライ付きフェッチを使用
                const response = await this._fetchWithRetry(url, options);

                // HTML返却によるJSONパースエラーを防ぐための事前チェック
                if (!response.ok && [502, 503, 504].includes(response.status)) {
                    throw new Error(`Server temporarily unavailable (Status: ${response.status})`);
                }

                const json = await response.json(); // JSON のレスポンスを待つ

                if (json && json.data && json.data.status) {
                    if (json.data.status === 'app_token_error') {
                        const loginCookieName = process.env.LOGIN_COOKIE_NAME;
                        document.cookie = `${loginCookieName}=; max-age=0; path=/`;
                        window.location.href = process.env.DEV_URL_BASE;
                    } else if (
                        json.data.status === 'user_suspended' ||
                        json.data.status === 'user_deleted' ||
                        json.data.status === 'user_banned'
                    ) {
                        alertStore.dispatch(setAlert(json.message));
                        return Promise.reject(json.message);
                    }
                } else if (json.code !== 200) { // API エラーの場合
                    if (json.status === 'token_expired_dashboard_init') {
                        return Promise.reject(new Error('Token expired for dashboard-init'));
                    }
                    // エラーメッセージを alert で表示
                    console.error(`[API Error Detail] Status: ${json.status}, Message: ${json.message}, Details:`, json.details);
                    alertStore.dispatch(setAlert(json.message || 'API エラーが発生しました'));
                    // エラーを reject して呼び出し元に伝える
                    return Promise.reject(new Error(json.message || 'API エラーが発生しました'));
                } else {
                    return Promise.resolve(json); // 成功時は json を返す
                }
            } catch (error) {
                alertStore.dispatch(setAlert('エラーが発生しました\nしばらくしてから再試行してください'));
                return Promise.reject(error); // ネットワークエラーなどは error を reject
            }
        } else {
            //window.location.reload();
            // process.env.DEV_URL_BASEに画面遷移する
            window.location.href = process.env.DEV_URL_BASE;
        }
    }


    /**
     * 認証済みのエンドポイントにフェッチ（ダウンロード用途など）
     * @param {*} endpoint 
     * @param {*} options 
     * @returns 
     */
    async fetchWithAuthDownload(endpoint, options) {
        //クッキーからpwappy_loginを取得
        const loginCookieName = process.env.LOGIN_COOKIE_NAME;
        const isLogin = document.cookie.split(';').some((item) => item.trim().startsWith(loginCookieName + '='));
        if (isLogin) {
            if (!options['mode']) {
                options['mode'] = 'cors'
            }
            if (!options['credentials']) {
                options['credentials'] = 'include'
            }
            //optionsのheadersを設定
            if (!options['headers']) {
                options['headers'] = {}
            }
            if (!options['headers']['Content-Type']) {
                options['headers']['Content-Type'] = 'application/json'
            }

            try {
                const url = this._getResolvedUrl(endpoint, options);

                // 通常のfetchの代わりにリトライ付きフェッチを使用
                const response = await this._fetchWithRetry(url, options);

                // ダウンロードリクエストでも、最終的に50xエラーのままなら弾く
                if (!response.ok && [502, 503, 504].includes(response.status)) {
                    throw new Error(`Server temporarily unavailable (Status: ${response.status})`);
                }

                //let blob = await response.blob();
                return Promise.resolve(response);
            } catch (error) {
                alertStore.dispatch(setAlert('エラーが発生しました\nしばらくしてから再試行してください'));
                return Promise.reject(new Error('Network or response reading error'));
            }
        } else {
            alertStore.dispatch(setAlert('トークンが有効ではありません\nもう一度ログインしてください'));
            //window.location.reload();
            // process.env.DEV_URL_BASEに画面遷移する
            window.location.href = process.env.DEV_URL_BASE;
        }
    }

    /**
     * 認証していない場合のエンドポイントにフェッチ
     * @param {*} endpoint 
     * @param {*} options 
     * @returns 
     */
    async fetchWithNonAuth(endpoint, options) {
        if (!options['mode']) {
            options['mode'] = 'cors'
        }
        if (!options['credentials']) {
            options['credentials'] = 'include'
        }
        //optionsのheadersを設定
        if (!options['headers']) {
            options['headers'] = {}
        }
        if (!options['headers']['Content-Type']) {
            options['headers']['Content-Type'] = 'application/json'
        }

        try {
            const url = this._getResolvedUrl(endpoint, options);

            // 通常のfetchの代わりにリトライ付きフェッチを使用
            const response = await this._fetchWithRetry(url, options);

            // Nginxエラーページ（HTML）をJSONパースしてSyntaxErrorになるのを防ぐ
            if (!response.ok && [502, 503, 504, 507].includes(response.status)) {
                throw new Error(`Server temporarily unavailable (Status: ${response.status})`);
            }

            const json = await response.json(); // JSON のレスポンスを待つ
            return Promise.resolve(json);
        } catch (error) {
            alertStore.dispatch(setAlert('エラーが発生しました\nしばらくしてから再試行してください'));
            return Promise.reject(new Error('Network or response reading error'));
        }
    }

};